import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Receipt, 
  Plus,
  Search,
  Download,
  Eye,
  Edit3,
  Trash2,
  X,
  Check,
  Calendar,
  TrendingUp,
  TrendingDown,
  FileText,
  User,
  Home,
  CreditCard,
  Banknote,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Building2,
  Percent,
  Loader
} from 'lucide-react';
import invoiceService from '../services/invoiceService';
import { formatPrice } from '../utils/helpers';
import PermissionGate from '../components/PermissionGate';
import ConfirmDialog from '../components/ConfirmDialog/ConfirmDialog';
import { useToast } from '../context/ToastContext';
import './InvoicesPage.css';

const InvoicesPage = () => {
  useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  // API state
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fetch invoices from API
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterType && filterType !== 'all') params.type = filterType;
      
      const response = await invoiceService.getAll(params);
      if (response.success) {
        setInvoices(response.data.invoices || response.data || []);
      } else {
        setError(response.message || 'Failed to fetch invoices');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const [newInvoice, setNewInvoice] = useState({
    type: 'income',
    category: 'commission',
    title: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'pending',
    client: {
      name: '',
      phone: ''
    },
    property: {
      title: '',
      price: ''
    },
    paymentMethod: 'cash',
    notes: ''
  });

  const categories = {
    income: [
      { value: 'commission', label: 'عمولة بيع/تأجير', icon: Percent },
      { value: 'consultation', label: 'رسوم استشارات', icon: FileText },
      { value: 'other', label: 'إيرادات أخرى', icon: Wallet }
    ],
    expense: [
      { value: 'marketing', label: 'تسويق وإعلان', icon: TrendingUp },
      { value: 'subscription', label: 'اشتراكات', icon: CreditCard },
      { value: 'office', label: 'مصاريف مكتبية', icon: Building2 },
      { value: 'other', label: 'مصاريف أخرى', icon: Banknote }
    ]
  };

  const paymentMethods = [
    { value: 'cash', label: 'نقدي', icon: Banknote },
    { value: 'bank_transfer', label: 'تحويل بنكي', icon: Building2 },
    { value: 'credit_card', label: 'بطاقة ائتمان', icon: CreditCard },
    { value: 'other', label: 'أخرى', icon: FileText }
  ];

  const statusOptions = [
    { value: 'paid', label: 'مدفوع', color: '#10b981' },
    { value: 'pending', label: 'قيد الانتظار', color: '#f59e0b' },
    { value: 'overdue', label: 'متأخر', color: '#ef4444' },
    { value: 'cancelled', label: 'ملغي', color: '#6b7280' }
  ];

  // Calculate statistics
  const stats = useMemo(() => {
    const totalIncome = invoices
      .filter(inv => inv.type === 'income' && inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.amount, 0);
    
    const totalExpenses = invoices
      .filter(inv => inv.type === 'expense' && inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.amount, 0);
    
    const pendingIncome = invoices
      .filter(inv => inv.type === 'income' && inv.status === 'pending')
      .reduce((sum, inv) => sum + inv.amount, 0);
    
    const overdueAmount = invoices
      .filter(inv => inv.status === 'overdue')
      .reduce((sum, inv) => sum + inv.amount, 0);

    const netProfit = totalIncome - totalExpenses;
    
    const thisMonthIncome = invoices
      .filter(inv => {
        const invDate = new Date(inv.date);
        const now = new Date();
        return inv.type === 'income' && 
               inv.status === 'paid' &&
               invDate.getMonth() === now.getMonth() &&
               invDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, inv) => sum + inv.amount, 0);

    return {
      totalIncome,
      totalExpenses,
      pendingIncome,
      overdueAmount,
      netProfit,
      thisMonthIncome,
      invoiceCount: invoices.length,
      paidCount: invoices.filter(inv => inv.status === 'paid').length,
      pendingCount: invoices.filter(inv => inv.status === 'pending').length,
      overdueCount: invoices.filter(inv => inv.status === 'overdue').length
    };
  }, [invoices]);

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const invoiceId = invoice.invoiceNumber || invoice._id || invoice.id || '';
      const matchesSearch = invoice.title?.includes(searchQuery) || 
                           invoiceId.includes(searchQuery) ||
                           invoice.client?.name?.includes(searchQuery);
      const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
      const matchesType = filterType === 'all' || invoice.type === filterType;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [invoices, searchQuery, filterStatus, filterType]);

  const handleCreateInvoice = useCallback(async () => {
    if (!newInvoice.title || !newInvoice.amount) {
      toast.warning('يرجى ملء الحقول المطلوبة');
      return;
    }

    setSaving(true);
    try {
      const invoiceData = {
        type: newInvoice.type,
        category: newInvoice.category,
        title: newInvoice.title,
        description: newInvoice.description || '',
        amount: parseFloat(newInvoice.amount),
        date: newInvoice.date || new Date().toISOString().split('T')[0],
        dueDate: newInvoice.dueDate || null,
        status: newInvoice.status || 'pending',
        paymentMethod: newInvoice.paymentMethod || 'cash',
        clientName: newInvoice.client?.name || '',
        clientPhone: newInvoice.client?.phone || '',
        notes: newInvoice.notes || ''
      };

      const response = await invoiceService.create(invoiceData);
      if (response.success) {
        setInvoices(prev => [response.data.invoice || response.data, ...prev]);
        setShowCreateModal(false);
        resetForm();
        toast.success('تم إنشاء الفاتورة بنجاح!');
      } else {
        toast.error(response.message || 'فشل في إنشاء الفاتورة');
      }
    } catch (err) {
      toast.error(err.message || 'فشل في إنشاء الفاتورة');
    } finally {
      setSaving(false);
    }
  }, [newInvoice, toast]);

  const handleEditInvoice = useCallback(async () => {
    if (!selectedInvoice.title || !selectedInvoice.amount) {
      toast.warning('يرجى ملء الحقول المطلوبة');
      return;
    }

    const invoiceId = selectedInvoice._id || selectedInvoice.id;
    setSaving(true);
    try {
      const updateData = {
        type: selectedInvoice.type,
        category: selectedInvoice.category,
        title: selectedInvoice.title,
        description: selectedInvoice.description,
        amount: parseFloat(selectedInvoice.amount),
        date: selectedInvoice.date,
        dueDate: selectedInvoice.dueDate,
        status: selectedInvoice.status,
        paymentMethod: selectedInvoice.paymentMethod,
        clientName: selectedInvoice.clientName || selectedInvoice.client?.name || '',
        clientPhone: selectedInvoice.clientPhone || selectedInvoice.client?.phone || '',
        notes: selectedInvoice.notes
      };
      
      const response = await invoiceService.update(invoiceId, updateData);
      if (response.success) {
        setInvoices(prev => prev.map(inv => 
          (inv._id || inv.id) === invoiceId ? (response.data.invoice || response.data) : inv
        ));
        setShowEditModal(false);
        toast.success('تم تحديث الفاتورة بنجاح!');
      } else {
        toast.error(response.message || 'فشل في تحديث الفاتورة');
      }
    } catch (err) {
      toast.error(err.message || 'فشل في تحديث الفاتورة');
    } finally {
      setSaving(false);
    }
  }, [selectedInvoice, toast]);

  const handleOpenDeleteDialog = (invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete) return;
    const id = invoiceToDelete._id || invoiceToDelete.id;
    try {
      const response = await invoiceService.delete(id);
      if (response.success) {
        setInvoices(prev => prev.filter(inv => (inv._id || inv.id) !== id));
        toast.success('تم حذف الفاتورة بنجاح!');
      } else {
        toast.error(response.message || 'فشل في حذف الفاتورة');
      }
    } catch (err) {
      toast.error(err.message || 'فشل في حذف الفاتورة');
    } finally {
      setShowDeleteDialog(false);
      setInvoiceToDelete(null);
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleEditClick = (invoice) => {
    setSelectedInvoice({ ...invoice });
    setShowEditModal(true);
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const response = await invoiceService.update(id, { status });
      if (response.success) {
        setInvoices(prev => prev.map(inv => 
          (inv._id || inv.id) === id ? { ...inv, status } : inv
        ));
        toast.success('تم تحديث الحالة بنجاح!');
      } else {
        toast.error(response.message || 'فشل في تحديث الحالة');
      }
    } catch (err) {
      toast.error(err.message || 'فشل في تحديث الحالة');
    }
  };

  const resetForm = () => {
    setNewInvoice({
      type: 'income',
      category: 'commission',
      title: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      status: 'pending',
      client: { name: '', phone: '' },
      property: { title: '', price: '' },
      paymentMethod: 'cash',
      notes: ''
    });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'paid': return <CheckCircle size={16} />;
      case 'pending': return <Clock size={16} />;
      case 'overdue': return <AlertCircle size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return null;
    }
  };

  const getCategoryLabel = (type, category) => {
    const cats = categories[type] || [];
    return cats.find(c => c.value === category)?.label || category;
  };

  // Export functionality
  const handleExportInvoices = useCallback(() => {
    if (filteredInvoices.length === 0) {
      toast.warning('لا توجد فواتير للتصدير');
      return;
    }

    try {
      // Create CSV content
      const headers = ['رقم الفاتورة', 'العنوان', 'النوع', 'الفئة', 'المبلغ', 'الحالة', 'التاريخ', 'العميل', 'طريقة الدفع'];
      
      const rows = filteredInvoices.map(invoice => [
        invoice.invoiceNumber || invoice._id || invoice.id || '',
        invoice.title || '',
        invoice.type === 'income' ? 'إيرادات' : 'مصروفات',
        getCategoryLabel(invoice.type, invoice.category),
        invoice.amount || 0,
        statusOptions.find(s => s.value === invoice.status)?.label || invoice.status,
        invoice.date ? new Date(invoice.date).toLocaleDateString('ar-EG', { calendar: 'gregory' }) : '',
        invoice.clientName || invoice.client?.name || '',
        paymentMethods.find(p => p.value === invoice.paymentMethod)?.label || invoice.paymentMethod || ''
      ]);

      // Add BOM for Arabic support in Excel
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `فواتير_${new Date().toLocaleDateString('ar-EG', { calendar: 'gregory' })}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('تم تصدير الفواتير بنجاح!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('فشل في تصدير الفواتير');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInvoices, toast]);

  const getPaymentMethodLabel = (method) => {
    return paymentMethods.find(m => m.value === method)?.label || method;
  };

  return (
    <PermissionGate feature="PAGE_INVOICES" type="page">
    <div className="invoices-page">
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => { setShowDeleteDialog(false); setInvoiceToDelete(null); }}
        onConfirm={handleConfirmDelete}
        title="حذف الفاتورة"
        message={`هل أنت متأكد من حذف الفاتورة "${invoiceToDelete?.title}"؟ لا يمكن التراجع عن هذا الإجراء.`}
        confirmText="حذف"
        cancelText="إلغاء"
        type="danger"
      />

      <div className="page-header">
        <div className="header-title">
          <Receipt size={28} />
          <div>
            <h1>الفواتير والتعاملات</h1>
            <p>إدارة الفواتير والإيرادات والمصروفات</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={20} />
          إضافة فاتورة
        </button>
      </div>

      {/* Stats Cards */}
      <div className="invoice-stats-grid">
        <div className="invoice-stat-card income">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatPrice(stats.totalIncome)}</span>
            <span className="stat-label">إجمالي الإيرادات</span>
          </div>
          <div className="stat-badge positive">
            <ArrowUpRight size={14} />
            مدفوع
          </div>
        </div>

        <div className="invoice-stat-card expense">
          <div className="stat-icon">
            <TrendingDown size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatPrice(stats.totalExpenses)}</span>
            <span className="stat-label">إجمالي المصروفات</span>
          </div>
          <div className="stat-badge negative">
            <ArrowDownRight size={14} />
            مدفوع
          </div>
        </div>

        <div className="invoice-stat-card profit">
          <div className="stat-icon">
            <Wallet size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatPrice(stats.netProfit)}</span>
            <span className="stat-label">صافي الربح</span>
          </div>
          <div className={`stat-badge ${stats.netProfit >= 0 ? 'positive' : 'negative'}`}>
            {stats.netProfit >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {stats.netProfit >= 0 ? 'ربح' : 'خسارة'}
          </div>
        </div>

        <div className="invoice-stat-card pending">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatPrice(stats.pendingIncome)}</span>
            <span className="stat-label">قيد التحصيل</span>
          </div>
          <div className="stat-badge warning">
            <Clock size={14} />
            {stats.pendingCount} فاتورة
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-invoice-stats">
        <div className="quick-stat">
          <span className="value">{stats.invoiceCount}</span>
          <span className="label">إجمالي الفواتير</span>
        </div>
        <div className="quick-stat">
          <span className="value paid">{stats.paidCount}</span>
          <span className="label">مدفوع</span>
        </div>
        <div className="quick-stat">
          <span className="value pending">{stats.pendingCount}</span>
          <span className="label">قيد الانتظار</span>
        </div>
        <div className="quick-stat">
          <span className="value overdue">{stats.overdueCount}</span>
          <span className="label">متأخر</span>
        </div>
        <div className="quick-stat">
          <span className="value month">{formatPrice(stats.thisMonthIncome)}</span>
          <span className="label">إيراد هذا الشهر</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input 
            type="text"
            placeholder="بحث في الفواتير..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filters">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">كل الأنواع</option>
            <option value="income">إيرادات</option>
            <option value="expense">مصروفات</option>
          </select>
          
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">كل الحالات</option>
            <option value="paid">مدفوع</option>
            <option value="pending">قيد الانتظار</option>
            <option value="overdue">متأخر</option>
            <option value="cancelled">ملغي</option>
          </select>

          <button className="btn btn-outline" onClick={handleExportInvoices}>
            <Download size={18} />
            تصدير
          </button>
        </div>
      </div>

      {/* Invoices List */}
      <div className="invoices-list">
        {loading ? (
          <div className="loading-state">
            <Loader className="spinner" size={40} />
            <p>جاري تحميل الفواتير...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <AlertCircle size={64} />
            <h3>حدث خطأ</h3>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchInvoices}>
              إعادة المحاولة
            </button>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="empty-state">
            <Receipt size={64} />
            <h3>لا توجد فواتير</h3>
            <p>ابدأ بإضافة فاتورتك الأولى</p>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={20} />
              إضافة فاتورة
            </button>
          </div>
        ) : (
          filteredInvoices.map(invoice => (
            <div key={invoice._id || invoice.id} className={`invoice-card ${invoice.type}`}>
              <div className="invoice-type-indicator">
                {invoice.type === 'income' ? (
                  <ArrowUpRight size={20} />
                ) : (
                  <ArrowDownRight size={20} />
                )}
              </div>
              
              <div className="invoice-main">
                <div className="invoice-header">
                  <span className="invoice-id">{invoice.invoiceNumber || invoice.id}</span>
                  <span className={`invoice-status ${invoice.status}`}>
                    {getStatusIcon(invoice.status)}
                    {statusOptions.find(s => s.value === invoice.status)?.label}
                  </span>
                </div>
                
                <h3 className="invoice-title">{invoice.title}</h3>
                
                <div className="invoice-meta">
                  <span className="meta-item">
                    <Calendar size={14} />
                    {new Date(invoice.date).toLocaleDateString('ar-EG', { calendar: 'gregory' })}
                  </span>
                  <span className="meta-item">
                    <FileText size={14} />
                    {getCategoryLabel(invoice.type, invoice.category)}
                  </span>
                  {(invoice.clientName || invoice.client?.name) && (
                    <span className="meta-item">
                      <User size={14} />
                      {invoice.clientName || invoice.client?.name}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="invoice-amount">
                <span className={`amount ${invoice.type}`}>
                  {invoice.type === 'income' ? '+' : '-'}
                  {formatPrice(invoice.amount)}
                </span>
                <span className="payment-method">
                  {getPaymentMethodLabel(invoice.paymentMethod)}
                </span>
              </div>
              
              <div className="invoice-actions">
                <button className="action-btn" onClick={() => handleViewInvoice(invoice)} title="عرض">
                  <Eye size={18} />
                </button>
                <button className="action-btn" onClick={() => handleEditClick(invoice)} title="تعديل">
                  <Edit3 size={18} />
                </button>
                <button className="action-btn delete" onClick={() => handleOpenDeleteDialog(invoice)} title="حذف">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Plus size={24} />
                إضافة فاتورة جديدة
              </h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Type Selection */}
              <div className="type-selector">
                <button 
                  className={`type-btn income ${newInvoice.type === 'income' ? 'active' : ''}`}
                  onClick={() => setNewInvoice({ ...newInvoice, type: 'income', category: 'commission' })}
                >
                  <ArrowUpRight size={20} />
                  إيراد
                </button>
                <button 
                  className={`type-btn expense ${newInvoice.type === 'expense' ? 'active' : ''}`}
                  onClick={() => setNewInvoice({ ...newInvoice, type: 'expense', category: 'marketing' })}
                >
                  <ArrowDownRight size={20} />
                  مصروف
                </button>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>عنوان الفاتورة *</label>
                  <input 
                    type="text"
                    className="form-control"
                    value={newInvoice.title}
                    onChange={(e) => setNewInvoice({ ...newInvoice, title: e.target.value })}
                    placeholder="مثال: عمولة بيع فيلا"
                  />
                </div>

                <div className="form-group">
                  <label>التصنيف *</label>
                  <select 
                    className="form-control"
                    value={newInvoice.category}
                    onChange={(e) => setNewInvoice({ ...newInvoice, category: e.target.value })}
                  >
                    {categories[newInvoice.type]?.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>المبلغ (ر.س) *</label>
                  <input 
                    type="number"
                    className="form-control"
                    value={newInvoice.amount}
                    onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>طريقة الدفع</label>
                  <select 
                    className="form-control"
                    value={newInvoice.paymentMethod}
                    onChange={(e) => setNewInvoice({ ...newInvoice, paymentMethod: e.target.value })}
                  >
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>تاريخ الفاتورة</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={newInvoice.date}
                    onChange={(e) => setNewInvoice({ ...newInvoice, date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>تاريخ الاستحقاق</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={newInvoice.dueDate}
                    onChange={(e) => setNewInvoice({ ...newInvoice, dueDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>الحالة</label>
                  <select 
                    className="form-control"
                    value={newInvoice.status}
                    onChange={(e) => setNewInvoice({ ...newInvoice, status: e.target.value })}
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Client Info (for income) */}
              {newInvoice.type === 'income' && (
                <div className="form-section">
                  <h4><User size={18} /> معلومات العميل</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>اسم العميل</label>
                      <input 
                        type="text"
                        className="form-control"
                        value={newInvoice.client.name}
                        onChange={(e) => setNewInvoice({ 
                          ...newInvoice, 
                          client: { ...newInvoice.client, name: e.target.value }
                        })}
                        placeholder="اسم العميل"
                      />
                    </div>
                    <div className="form-group">
                      <label>رقم الجوال</label>
                      <input 
                        type="tel"
                        className="form-control"
                        value={newInvoice.client.phone}
                        onChange={(e) => setNewInvoice({ 
                          ...newInvoice, 
                          client: { ...newInvoice.client, phone: e.target.value }
                        })}
                        placeholder="05xxxxxxxx"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Property Info (for commission) */}
              {newInvoice.category === 'commission' && (
                <div className="form-section">
                  <h4><Home size={18} /> معلومات العقار</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>اسم العقار</label>
                      <input 
                        type="text"
                        className="form-control"
                        value={newInvoice.property.title}
                        onChange={(e) => setNewInvoice({ 
                          ...newInvoice, 
                          property: { ...newInvoice.property, title: e.target.value }
                        })}
                        placeholder="مثال: فيلا حي الحجاز"
                      />
                    </div>
                    <div className="form-group">
                      <label>قيمة الصفقة</label>
                      <input 
                        type="number"
                        className="form-control"
                        value={newInvoice.property.price}
                        onChange={(e) => setNewInvoice({ 
                          ...newInvoice, 
                          property: { ...newInvoice.property, price: e.target.value }
                        })}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group full-width">
                <label>الوصف</label>
                <textarea 
                  className="form-control"
                  rows={2}
                  value={newInvoice.description}
                  onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                  placeholder="وصف إضافي..."
                />
              </div>

              <div className="form-group full-width">
                <label>ملاحظات</label>
                <textarea 
                  className="form-control"
                  rows={2}
                  value={newInvoice.notes}
                  onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                  placeholder="ملاحظات خاصة..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCreateModal(false)}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleCreateInvoice}>
                <Check size={18} />
                إنشاء الفاتورة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Receipt size={24} />
                تفاصيل الفاتورة
              </h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="invoice-view-header">
                <div className={`invoice-type-badge ${selectedInvoice.type}`}>
                  {selectedInvoice.type === 'income' ? (
                    <><ArrowUpRight size={18} /> إيراد</>
                  ) : (
                    <><ArrowDownRight size={18} /> مصروف</>
                  )}
                </div>
                <span className="invoice-view-id">{selectedInvoice.invoiceNumber || selectedInvoice._id || selectedInvoice.id}</span>
              </div>

              <div className={`invoice-view-amount ${selectedInvoice.type}`}>
                {selectedInvoice.type === 'income' ? '+' : '-'}
                {formatPrice(selectedInvoice.amount)}
              </div>

              <div className={`invoice-view-status ${selectedInvoice.status}`}>
                {getStatusIcon(selectedInvoice.status)}
                {statusOptions.find(s => s.value === selectedInvoice.status)?.label}
              </div>

              <div className="invoice-view-details">
                <div className="detail-row">
                  <span className="detail-label">العنوان</span>
                  <span className="detail-value">{selectedInvoice.title}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">التصنيف</span>
                  <span className="detail-value">{getCategoryLabel(selectedInvoice.type, selectedInvoice.category)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">تاريخ الفاتورة</span>
                  <span className="detail-value">{new Date(selectedInvoice.date).toLocaleDateString('ar-EG', { calendar: 'gregory' })}</span>
                </div>
                {selectedInvoice.dueDate && (
                  <div className="detail-row">
                    <span className="detail-label">تاريخ الاستحقاق</span>
                    <span className="detail-value">{new Date(selectedInvoice.dueDate).toLocaleDateString('ar-EG', { calendar: 'gregory' })}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">طريقة الدفع</span>
                  <span className="detail-value">{getPaymentMethodLabel(selectedInvoice.paymentMethod)}</span>
                </div>
                
                {(selectedInvoice.clientName || selectedInvoice.client?.name) && (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">العميل</span>
                      <span className="detail-value">{selectedInvoice.clientName || selectedInvoice.client?.name}</span>
                    </div>
                    {(selectedInvoice.clientPhone || selectedInvoice.client?.phone) && (
                      <div className="detail-row">
                        <span className="detail-label">الجوال</span>
                        <span className="detail-value">{selectedInvoice.clientPhone || selectedInvoice.client?.phone}</span>
                      </div>
                    )}
                  </>
                )}

                {(selectedInvoice.relatedOffer || selectedInvoice.property?.title) && (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">العقار</span>
                      <span className="detail-value">{selectedInvoice.relatedOffer?.title || selectedInvoice.property?.title}</span>
                    </div>
                    {(selectedInvoice.relatedOffer?.price || selectedInvoice.property?.price) && (
                      <div className="detail-row">
                        <span className="detail-label">قيمة الصفقة</span>
                        <span className="detail-value">{formatPrice(selectedInvoice.relatedOffer?.price || selectedInvoice.property?.price)}</span>
                      </div>
                    )}
                  </>
                )}

                {selectedInvoice.description && (
                  <div className="detail-row full">
                    <span className="detail-label">الوصف</span>
                    <span className="detail-value">{selectedInvoice.description}</span>
                  </div>
                )}

                {selectedInvoice.notes && (
                  <div className="detail-row full">
                    <span className="detail-label">ملاحظات</span>
                    <span className="detail-value">{selectedInvoice.notes}</span>
                  </div>
                )}
              </div>

              {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                <div className="status-actions">
                  <span>تغيير الحالة:</span>
                  <button 
                    className="status-btn paid"
                    onClick={() => {
                      handleUpdateStatus(selectedInvoice._id || selectedInvoice.id, 'paid');
                      setShowViewModal(false);
                    }}
                  >
                    <CheckCircle size={16} />
                    مدفوع
                  </button>
                  <button 
                    className="status-btn cancelled"
                    onClick={() => {
                      handleUpdateStatus(selectedInvoice._id || selectedInvoice.id, 'cancelled');
                      setShowViewModal(false);
                    }}
                  >
                    <XCircle size={16} />
                    إلغاء
                  </button>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowViewModal(false)}>
                إغلاق
              </button>
              <button className="btn btn-primary" onClick={() => {
                setShowViewModal(false);
                handleEditClick(selectedInvoice);
              }}>
                <Edit3 size={18} />
                تعديل
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Edit3 size={24} />
                تعديل الفاتورة
              </h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Type Selection */}
              <div className="type-selector">
                <button 
                  className={`type-btn income ${selectedInvoice.type === 'income' ? 'active' : ''}`}
                  onClick={() => setSelectedInvoice({ ...selectedInvoice, type: 'income', category: 'commission' })}
                >
                  <ArrowUpRight size={20} />
                  إيراد
                </button>
                <button 
                  className={`type-btn expense ${selectedInvoice.type === 'expense' ? 'active' : ''}`}
                  onClick={() => setSelectedInvoice({ ...selectedInvoice, type: 'expense', category: 'marketing' })}
                >
                  <ArrowDownRight size={20} />
                  مصروف
                </button>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>عنوان الفاتورة *</label>
                  <input 
                    type="text"
                    className="form-control"
                    value={selectedInvoice.title || ''}
                    onChange={(e) => setSelectedInvoice({ ...selectedInvoice, title: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>التصنيف *</label>
                  <select 
                    className="form-control"
                    value={selectedInvoice.category || ''}
                    onChange={(e) => setSelectedInvoice({ ...selectedInvoice, category: e.target.value })}
                  >
                    {categories[selectedInvoice.type]?.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>المبلغ (ر.س) *</label>
                  <input 
                    type="number"
                    className="form-control"
                    value={selectedInvoice.amount || ''}
                    onChange={(e) => setSelectedInvoice({ ...selectedInvoice, amount: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>طريقة الدفع</label>
                  <select 
                    className="form-control"
                    value={selectedInvoice.paymentMethod || 'cash'}
                    onChange={(e) => setSelectedInvoice({ ...selectedInvoice, paymentMethod: e.target.value })}
                  >
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>تاريخ الفاتورة</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={selectedInvoice.date ? new Date(selectedInvoice.date).toISOString().split('T')[0] : ''}
                    onChange={(e) => setSelectedInvoice({ ...selectedInvoice, date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>تاريخ الاستحقاق</label>
                  <input 
                    type="date"
                    className="form-control"
                    value={selectedInvoice.dueDate ? new Date(selectedInvoice.dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setSelectedInvoice({ ...selectedInvoice, dueDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>الحالة</label>
                  <select 
                    className="form-control"
                    value={selectedInvoice.status || 'pending'}
                    onChange={(e) => setSelectedInvoice({ ...selectedInvoice, status: e.target.value })}
                  >
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Client Info (for income) */}
              {selectedInvoice.type === 'income' && (
                <div className="form-section">
                  <h4><User size={18} /> معلومات العميل</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>اسم العميل</label>
                      <input 
                        type="text"
                        className="form-control"
                        value={selectedInvoice.clientName || ''}
                        onChange={(e) => setSelectedInvoice({ ...selectedInvoice, clientName: e.target.value })}
                        placeholder="اسم العميل"
                      />
                    </div>
                    <div className="form-group">
                      <label>رقم الجوال</label>
                      <input 
                        type="tel"
                        className="form-control"
                        value={selectedInvoice.clientPhone || ''}
                        onChange={(e) => setSelectedInvoice({ ...selectedInvoice, clientPhone: e.target.value })}
                        placeholder="05xxxxxxxx"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group full-width">
                <label>الوصف</label>
                <textarea 
                  className="form-control"
                  rows={2}
                  value={selectedInvoice.description || ''}
                  onChange={(e) => setSelectedInvoice({ ...selectedInvoice, description: e.target.value })}
                  placeholder="وصف إضافي..."
                />
              </div>

              <div className="form-group full-width">
                <label>ملاحظات</label>
                <textarea 
                  className="form-control"
                  rows={2}
                  value={selectedInvoice.notes || ''}
                  onChange={(e) => setSelectedInvoice({ ...selectedInvoice, notes: e.target.value })}
                  placeholder="ملاحظات خاصة..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowEditModal(false)}>
                إلغاء
              </button>
              <button className="btn btn-primary" onClick={handleEditInvoice} disabled={saving}>
                {saving ? <Loader className="spinner" size={18} /> : <Check size={18} />}
                حفظ التعديلات
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </PermissionGate>
  );
};

export default InvoicesPage;
